import { useFocusWithin } from '@react-aria/interactions';
import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  InputGroup,
  InputGroupInput,
  InputGroupSlot
} from 'v5/design-system/InputGroup';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from 'v5/design-system/Select';
import { assert } from 'v5/platform/lang/assert';
import { InputProps } from '../../../../../v5/design-system/Input';
import { unitFieldUtils, MEASUREMENT_UNIT_OPTIONS } from './unit-field-utils';

export type UnitValue =
  | { unit: 'cm' | 'm'; amount: string }
  | { unit: 'in'; amount: string }
  | { unit: 'ft'; feet: string; inches: string };

export interface UnitFieldProps {
  value: UnitValue;
  onChange: (value: UnitValue) => void;
  onUnitChange?: (unit: UnitValue['unit']) => void;
  mode: 'edit' | 'view';
  disableUnitSelect?: boolean;
  onBlurWithin?: () => void;
  size?: InputProps['size'];
  variant?: InputProps['variant'];
  /**
   * Even if mode is 'edit', the field will be readonly if this is true
   */
  readOnly?: boolean;
}

const options = MEASUREMENT_UNIT_OPTIONS;

export const UnitField = forwardRef<HTMLDivElement, UnitFieldProps>(
  (
    {
      value,
      onChange,
      onUnitChange,
      mode,
      disableUnitSelect,
      onBlurWithin,
      variant = 'inline',
      size = 'sm',
      readOnly = false
    },
    ref
  ) => {
    if (mode === 'view') {
      return (
        <div className="text-base h-8 pt-1 align-middle">
          {unitFieldUtils.formatDisplay(value)}
        </div>
      );
    }

    const { t } = useTranslation('v4_shared');

    const tryRestoreAmount = (value: UnitValue) => {
      switch (value.unit) {
        case 'cm':
        case 'm':
          return value.amount.toString();
        case 'in':
          return value.amount.toString();
        case 'ft':
          return value.feet.toString();
      }
    };

    const handleUnitChange = (selected: { id: string }) => {
      onUnitChange?.(selected.id as UnitValue['unit']);
      switch (selected.id) {
        case 'cm':
        case 'm':
          onChange({ unit: selected.id, amount: tryRestoreAmount(value) });
          break;
        case 'in':
          onChange({ unit: 'in', amount: tryRestoreAmount(value) });
          break;
        case 'ft':
          onChange({ unit: 'ft', feet: tryRestoreAmount(value), inches: '0' });
          break;
        default:
          break;
      }
    };

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      assert('amount' in value);
      onChange({ ...value, amount: e.target.value });
    };

    const handleFeetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      assert('feet' in value);
      onChange({ ...value, feet: e.target.value });
    };

    const handleInchesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      assert('inches' in value);
      onChange({ ...value, inches: e.target.value });
    };

    const { focusWithinProps } = useFocusWithin({ onBlurWithin: onBlurWithin });

    return (
      <div className="@container" {...focusWithinProps} ref={ref}>
        <div className="flex items-center text-nowrap space-x-2 @max-[200px]:grid">
          {value.unit === 'ft' ? (
            <>
              <InputGroup
                variant={variant}
                size={size}
                className="flex-1"
                aria-readonly={readOnly}
              >
                <InputGroupInput
                  placeholder={t('UnitField.placeholders.feet')}
                  value={value.feet}
                  onChange={handleFeetChange}
                  readOnly={readOnly}
                />
                <InputGroupSlot>
                  <span>{t('UnitField.ft')}</span>
                </InputGroupSlot>
              </InputGroup>
              <InputGroup
                variant={variant}
                size={size}
                className="flex-1 text-nowrap"
                aria-readonly={readOnly}
              >
                <InputGroupInput
                  placeholder={t('UnitField.placeholders.inches')}
                  value={value.inches}
                  onChange={handleInchesChange}
                  readOnly={readOnly}
                />
                <InputGroupSlot>
                  <span>{t('UnitField.in')}</span>
                </InputGroupSlot>
              </InputGroup>
            </>
          ) : (
            <InputGroup
              variant={variant}
              size={size}
              className="flex-1 col-span-2 text-nowrap"
              aria-readonly={readOnly}
            >
              <InputGroupInput
                placeholder={t('UnitField.placeholders.amount')}
                value={value.amount}
                onChange={handleAmountChange}
                readOnly={readOnly}
              />
              <InputGroupSlot>
                <span>{t(`UnitField.${value.unit}`)}</span>
              </InputGroupSlot>
            </InputGroup>
          )}

          {disableUnitSelect ? (
            <></>
          ) : (
            <div className="shrink-0 w-[120px] col-span-2 @max-[200px]:w-auto">
              <Select
                value={value.unit}
                onValueChange={id => handleUnitChange({ id })}
              >
                <SelectTrigger
                  variant={variant}
                  size={size}
                  className="w-full whitespace-pre"
                  readOnly={readOnly}
                >
                  <SelectValue placeholder={t('UnitField.placeholders.unit')} />
                </SelectTrigger>
                <SelectContent>
                  {options.map(opt => (
                    <SelectItem key={opt} value={opt}>
                      {t(`UnitField.${opt}`, {
                        context: 'long'
                      })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
    );
  }
);
